import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useNavigate } from 'react-router-dom'

import { usePortfolioProjects } from '@/features/portfolio/api'
import { useCreateTask } from '@/features/tasks/api'
import { DueDatePicker } from '@/features/tasks/components/DueDatePicker'
import { Button, Field, Input, Portal, Select } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'

export interface QuickTaskDialogProps {
  open: boolean
  onClose: () => void
  /** Proyecto preseleccionado: si se abre desde la ficha de un proyecto. */
  projectId?: string
}

/**
 * Una tarea nueva desde cualquier pantalla, sin ir al proyecto.
 *
 * Lo mínimo para que exista: el título y dónde va. La fecha es opcional; el
 * resto —responsable, descripción, estado— se completa después en la tarea,
 * que para eso tiene su panel. Pedirlo acá haría lento lo que tiene que ser
 * rápido.
 *
 * Al crear, avisa con un link a la tarea en su proyecto, y el formulario queda
 * listo para la siguiente en el mismo proyecto: cargar varias de un tirón es
 * el caso común.
 */
export function QuickTaskDialog({ open, onClose, projectId }: QuickTaskDialogProps) {
  const t = useT()
  const navigate = useNavigate()
  const projects = usePortfolioProjects({})
  const [proyecto, setProyecto] = useState(projectId ?? '')
  const [titulo, setTitulo] = useState('')
  const [vence, setVence] = useState<string | null>(null)
  const campo = useRef<HTMLInputElement>(null)
  const create = useCreateTask(proyecto)

  useEffect(() => {
    if (!open) return
    if (projectId) setProyecto(projectId)
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose, projectId])

  // Permanentes incluidos: también juntan tareas.
  const opciones = useMemo(() => projects.data ?? [], [projects.data])
  const elegido = opciones.find((project) => project.id === proyecto)

  if (!open) return null

  const puede = Boolean(proyecto && titulo.trim()) && !create.isPending

  const crear = async () => {
    if (!puede) return
    const nombre = elegido?.name ?? ''
    await create.mutateAsync({
      project_id: proyecto,
      title: titulo.trim(),
      ...(vence ? { due_date: vence } : {}),
    })
    toast.success(t('quick.task.created', { proyecto: nombre }), {
      action: {
        label: t('quick.task.open'),
        onClick: () => void navigate(`/projects/${proyecto}/tareas`),
      },
    })
    setTitulo('')
    setVence(null)
    campo.current?.focus()
  }

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-start justify-center bg-bg/70 p-4 pt-[14vh]">
        <button
          type="button"
          aria-label={t('common.ui.closeDialog')}
          onClick={onClose}
          className="absolute inset-0 cursor-default"
        />
        <form
          role="dialog"
          aria-modal="true"
          aria-label={t('quick.task.title')}
          className="hairline relative flex w-full max-w-[460px] flex-col gap-3 rounded-[var(--radius-card)] bg-surface-1 p-4"
          onSubmit={(event) => {
            event.preventDefault()
            void crear()
          }}
        >
          <h2 className="font-display text-base text-text-1">{t('quick.task.title')}</h2>

          <Field label={t('quick.task.name')} htmlFor="quick-task-title">
            <Input
              id="quick-task-title"
              ref={campo}
              autoFocus
              value={titulo}
              maxLength={200}
              placeholder={t('quick.task.namePlaceholder')}
              onChange={(event) => setTitulo(event.target.value)}
            />
          </Field>

          <Field label={t('common.label.project')} htmlFor="quick-task-project">
            <Select
              id="quick-task-project"
              value={proyecto}
              disabled={projects.isPending}
              onChange={(event) => setProyecto(event.target.value)}
            >
              <option value="">{t('quick.task.pickProject')}</option>
              {opciones.map((project) => (
                <option key={project.id} value={project.id}>
                  {`${project.name} · ${project.company.name}`}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('tasks.field.dueDate')}>
            <DueDatePicker withShortcuts value={vence} onChange={setVence} />
          </Field>

          <div className="flex items-center justify-between gap-2">
            <span className="text-2xs text-text-3">{t('quick.task.hint')}</span>
            <div className="flex gap-2">
              <Button type="button" onClick={onClose}>
                {t('common.action.close')}
              </Button>
              <Button type="submit" variant="primary" disabled={!puede}>
                {t('quick.task.submit')}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Portal>
  )
}
